/**
 * DocumentDatabasesPage Component
 *
 * Main page for managing document databases with search functionality.
 */

import React, { useState, useMemo } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { DocumentDatabaseTable } from '../components/DocumentDatabaseTable';
import { CreateDocumentDatabaseModal } from '../components/CreateDocumentDatabaseModal';
import { UpdateDocumentDatabaseModal } from '../components/UpdateDocumentDatabaseModal';
import { DocumentDatabaseDetailsModal } from '../components/DocumentDatabaseDetailsModal';
import { DeleteDocumentDatabaseModal } from '../components/DeleteDocumentDatabaseModal';
import { useDocumentDatabases } from '../hooks/useDocumentDatabases';
import { useAuth } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../lib/utils/permissions';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import type { DocumentDatabaseSanitized } from '../../../types/document-database.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * DocumentDatabasesPage component
 */
export const DocumentDatabasesPage: React.FC = () => {
  const { user, tenant, activeRole } = useAuth();
  const layoutProps = useLayoutProps();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [selectedDocumentDB, setSelectedDocumentDB] = useState<DocumentDatabaseSanitized | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const tenantId = tenant?.tenantId || '';

  // Permission checks
  const canCreate = hasPermission(activeRole?.permissions, 'resources:documentdb:create');
  const canUpdate = hasPermission(activeRole?.permissions, 'resources:documentdb:update');
  const canDelete = hasPermission(activeRole?.permissions, 'resources:documentdb:delete');

  // Debounced search (300ms delay)
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch document databases
  const {
    data: documentDatabases = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useDocumentDatabases(tenantId, {
    search: debouncedSearch || undefined,
  });

  // Filter document databases locally (additional filtering if needed)
  const filteredDocumentDatabases = useMemo(() => {
    if (!documentDatabases) return [];

    let filtered = [...documentDatabases];

    // Search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.documentDBName.toLowerCase().includes(query) ||
          item.documentDBCode.toLowerCase().includes(query) ||
          (item.description && item.description.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [documentDatabases, debouncedSearch]);

  const handleViewDetails = (documentDB: DocumentDatabaseSanitized) => {
    setSelectedDocumentDB(documentDB);
    setShowDetailsModal(true);
  };

  const handleEdit = (documentDB: DocumentDatabaseSanitized) => {
    setSelectedDocumentDB(documentDB);
    setShowUpdateModal(true);
  };

  const handleDelete = (documentDB: DocumentDatabaseSanitized) => {
    setSelectedDocumentDB(documentDB);
    setShowDeleteModal(true);
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Document Databases</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Manage JSON document libraries and application access
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => setShowCreateModal(true)}>
              Create Database
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search document databases..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            {getErrorMessage(error)}
          </Alert>
        )}

        {/* Document Databases Table */}
        <DocumentDatabaseTable
          documentDatabases={filteredDocumentDatabases}
          isLoading={isLoading}
          onView={handleViewDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
          canEdit={canUpdate}
          canDelete={canDelete}
        />

        {/* Modals */}
        <CreateDocumentDatabaseModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          tenantId={tenantId}
        />

        {showDetailsModal && selectedDocumentDB && (
          <DocumentDatabaseDetailsModal
            isOpen={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedDocumentDB(null);
            }}
            tenantId={tenantId}
            documentDBId={selectedDocumentDB.documentDBId}
          />
        )}

        {showUpdateModal && selectedDocumentDB && (
          <UpdateDocumentDatabaseModal
            isOpen={showUpdateModal}
            onClose={() => {
              setShowUpdateModal(false);
              setSelectedDocumentDB(null);
            }}
            tenantId={tenantId}
            documentDB={selectedDocumentDB}
          />
        )}

        {showDeleteModal && selectedDocumentDB && (
          <DeleteDocumentDatabaseModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedDocumentDB(null);
            }}
            tenantId={tenantId}
            documentDB={selectedDocumentDB}
          />
        )}
      </div>
    </DashboardLayout>
  );
};
